/**
 * TimekeepersReportTab
 *
 * Fariha's monthly and weekly reports, merged. Both were the same figures over
 * a different window — "same as monthly, but with a focus on the current month
 * and previous week only" — so the period is a control rather than a tab, with
 * This Week and Last Week sitting alongside the month presets.
 *
 * Runs immediately on the current month for every timekeeper, then re-runs as
 * the period or selection changes. No Run button — the default answer is the
 * one people want, and waiting for a click to show it is friction.
 *
 * Expanding a row shows that timekeeper week by week. Those weeks are fetched
 * as soon as the main report lands, not on click, so expansion is instant.
 */

import { Check } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import {
	DATE_PRESET_LABELS,
	type DatePreset,
	getDateRange,
} from "@/features/attorney/hooks/useReportingDashboard";
import { ValueChecklist } from "@/features/attorney/views/Entries/filters/ValueChecklist";
import { cn } from "@/shared/lib/utils";
import { DatePicker } from "@/v2/components/date-picker/DatePicker";
import { Button } from "@/v2/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/v2/components/ui/popover";
import {
	useMonthlyReport,
	useTimekeeperOptions,
	useWeeklyBreakdown,
} from "../../hooks/useReportsData";
import type { GoalProgressRow, GoalsByGroup } from "../../types/reports";
import ReportKpiRow, { type KpiCardSpec } from "./ReportKpiRow";
import ReportTable, {
	formatHours,
	PaceBar,
	type ReportColumn,
	VarianceCell,
} from "./ReportTable";
import WeeklyHoursChart from "./WeeklyHoursChart";

interface TimekeepersReportTabProps {
	goals: GoalsByGroup;
}

const columns: Array<ReportColumn<GoalProgressRow>> = [
	{ key: "name", header: "Timekeeper", cell: (r) => r.profName },
	{
		key: "hours",
		header: "Hours",
		align: "right",
		width: "6rem",
		cell: (r) => formatHours(r.hours),
	},
	{
		key: "target",
		header: "Goal",
		align: "right",
		width: "6rem",
		cell: (r) =>
			r.target === null ? (
				<span className="text-muted-foreground">—</span>
			) : (
				formatHours(r.target)
			),
	},
	{
		key: "variance",
		header: "Variance",
		align: "right",
		width: "7rem",
		cell: (r) => <VarianceCell value={r.variance} />,
	},
	{
		key: "pace",
		header: "Pace",
		width: "12rem",
		cell: (r) => <PaceBar pct={r.pacePct} />,
	},
];

/** Current calendar month — the report's default window. */
function currentMonth(): { from: Date; to: Date } {
	const now = new Date();
	return {
		from: new Date(now.getFullYear(), now.getMonth(), 1),
		to: new Date(now.getFullYear(), now.getMonth() + 1, 0),
	};
}

export default function TimekeepersReportTab({
	goals,
}: TimekeepersReportTabProps) {
	const [dateRange, setDateRange] = useState(currentMonth);
	// null is implicit "all", matching ValueChecklist's contract.
	const [selected, setSelected] = useState<string[] | null>(null);

	const { options, loading: optionsLoading } = useTimekeeperOptions();

	// Derived, not gated behind a button: the report always reflects the controls.
	const params = useMemo(
		() => ({
			from: dateRange.from,
			to: dateRange.to,
			profIds: selected ?? [],
		}),
		[dateRange, selected],
	);

	const { data, loading, error } = useMonthlyReport(params, goals);

	const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
	const toggleRow = useCallback((key: string) => {
		setExpanded((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	}, []);

	// Prefetched the moment the main report lands, so expanding is instant.
	const weekly = useWeeklyBreakdown(
		dateRange.from,
		dateRange.to,
		Boolean(data),
	);

	const activePreset = useMemo(() => {
		const keys = Object.keys(DATE_PRESET_LABELS) as DatePreset[];
		return keys.find((key) => {
			const r = getDateRange(key);
			return (
				r.from.toDateString() === dateRange.from.toDateString() &&
				r.to.toDateString() === dateRange.to.toDateString()
			);
		});
	}, [dateRange]);

	const allValues = useMemo(() => options.map((o) => o.value), [options]);
	const selectedLabel =
		selected === null
			? optionsLoading
				? "Loading…"
				: `All timekeepers (${options.length})`
			: selected.length === 0
				? "None selected"
				: `${selected.length} selected`;

	function toggle(value: string) {
		setSelected((prev) => {
			// First click out of implicit-all deselects just that one.
			if (prev === null) return allValues.filter((v) => v !== value);
			return prev.includes(value)
				? prev.filter((v) => v !== value)
				: [...prev, value];
		});
	}

	const renderExpanded = useCallback(
		(row: GoalProgressRow) => {
			if (weekly.weeks.length === 0) {
				return weekly.loading ? (
					<p className="text-xs text-muted-foreground">Loading weeks…</p>
				) : null;
			}

			// The weekly goal is the period target scaled to one week's business
			// days, so a short week is not judged as a full one.
			const perWorkday =
				row.target !== null &&
				row.target > 0 &&
				(data?.totals.totalDays ?? 0) > 0
					? row.target / (data?.totals.totalDays ?? 1)
					: 0;

			const hours = weekly.hoursByProf.get(row.profId);
			const points = weekly.weeks.map((w, i) => ({
				label: w.label,
				// A missing entry means the endpoint dropped a zero-hour week, so
				// it renders as the zero it is rather than disappearing.
				hours: hours?.get(i) ?? 0,
				goal: perWorkday * w.workdays,
			}));

			// Weeks are equal length apart from the clipped ends, so a single
			// reference line is honest; use the modal full-week goal.
			const fullWeekGoal = perWorkday * 5;

			return (
				<div className="flex flex-col gap-2">
					<WeeklyHoursChart
						data={points}
						goal={fullWeekGoal > 0 ? fullWeekGoal : null}
					/>
					{weekly.truncated && (
						<p className="text-[11px] text-muted-foreground">
							Showing the first {weekly.weeks.length} weeks of a longer range.
						</p>
					)}
				</div>
			);
		},
		[weekly, data],
	);

	const cards: KpiCardSpec[] = data
		? [
				{
					label: "Hours Entered",
					value: formatHours(data.totals.hours),
					suffix: "hrs",
					gradient: "green",
				},
				{
					label: "Goal",
					value: formatHours(data.totals.target),
					suffix: "hrs",
					gradient: "teal",
				},
				{
					label: "Pace",
					value: data.totals.pacePct === null ? "—" : `${data.totals.pacePct}%`,
					gradient: "blue",
				},
				{
					label: "On Track",
					value: `${data.totals.onTrackCount} / ${data.totals.timekeeperCount}`,
					gradient: "dark",
				},
			]
		: [];

	return (
		<div className="flex flex-col gap-6">
			{/* Controls — no surrounding card, they sit directly above the results. */}
			<div className="flex flex-wrap items-end gap-3">
				<div className="flex flex-col gap-1.5">
					<span className="text-xs font-medium text-muted-foreground">
						Period
					</span>
					<DatePicker
						className="w-fit"
						dateRange={dateRange}
						onDateChange={(day) => setDateRange({ from: day, to: day })}
						onDateRangeChange={(range) =>
							setDateRange({ from: range.from, to: range.to })
						}
						selectionMode="range"
						sideSlot={({ selectRange }) => (
							<div className="flex w-[160px] flex-col border-r p-1">
								{Object.entries(DATE_PRESET_LABELS).map(([key, label]) => {
									const range = getDateRange(key as DatePreset);
									return (
										<button
											key={key}
											type="button"
											className={cn(
												"flex items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted",
												activePreset === key && "bg-muted font-medium",
											)}
											onClick={() => selectRange(range)}
										>
											<Check
												className={cn(
													"h-3.5 w-3.5 shrink-0",
													activePreset === key ? "opacity-100" : "opacity-0",
												)}
											/>
											{label}
										</button>
									);
								})}
							</div>
						)}
					/>
				</div>

				<div className="flex flex-col gap-1.5">
					<span className="text-xs font-medium text-muted-foreground">
						Timekeepers
					</span>
					{/* Popover closes on outside click and Escape, same as the
					    entries filter menu this checklist comes from. */}
					<Popover>
						<PopoverTrigger asChild>
							<Button
								variant="outline"
								className="w-64 justify-start font-normal"
							>
								{selectedLabel}
							</Button>
						</PopoverTrigger>
						<PopoverContent align="start" className="w-72 p-2">
							<ValueChecklist
								field="timekeeper"
								options={options}
								selectedValues={selected}
								onToggle={toggle}
								onSelectAll={() => setSelected(null)}
								onSelectNone={() => setSelected([])}
							/>
						</PopoverContent>
					</Popover>
				</div>
			</div>

			{/* Results */}
			{error && (
				<div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
					{error}
				</div>
			)}

			{loading && !data && (
				<div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">
					Loading…
				</div>
			)}

			{data && (
				<>
					{/* Baseline-aligned with the cards, so the note sits on the same
					    line as their lower edge rather than floating at the top. */}
					<div className="flex flex-wrap items-end justify-between gap-4">
						<ReportKpiRow cards={cards} />
						<p className="pb-1 text-xs text-muted-foreground">
							{data.totals.elapsedDays} of {data.totals.totalDays} business days
							elapsed
						</p>
					</div>
					<ReportTable
						columns={columns}
						rows={data.rows}
						rowKey={(r) => r.profId}
						isStale={loading}
						renderExpanded={renderExpanded}
						expandedKeys={expanded}
						onToggleRow={toggleRow}
						emptyMessage="No time entered by the selected timekeepers in this period."
					/>
				</>
			)}
		</div>
	);
}
